import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import { 
  CheckCircle as UsedIcon,
  Delete as DiscardedIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { repAPI } from '../../services/api';

const RepInventory = () => {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Update Status Dialog
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [updatingItem, setUpdatingItem] = useState(null);
  const [updateData, setUpdateData] = useState({
    status: '',
    quantityUsed: 0,
    quantityDiscarded: 0,
    notes: ''
  });

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const response = await repAPI.getRepInventory();
      if (response.success) {
        setInventory(response.data);
      }
    } catch (err) {
      setError('Failed to load inventory');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openUpdateDialog = (item, status = '') => {
    setUpdatingItem(item);
    setUpdateData({
      status: status || item.status || 'UNUSED',
      quantityUsed: item.quantityUsed || 0,
      quantityDiscarded: item.quantityDiscarded || 0,
      notes: item.notes || ''
    });
    setUpdateDialogOpen(true);
  };

  const handleUpdateStatus = async () => {
    try {
      if (!updatingItem) return;

      const response = await repAPI.updateClientInventory(updatingItem.id, updateData);
      if (response.success) {
        setSuccess('Inventory status updated successfully!');
        setUpdateDialogOpen(false);
        setUpdatingItem(null);
        fetchInventory();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.message || 'Failed to update inventory status');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      UNUSED: 'default',
      USED: 'success',
      DISCARDED: 'error',
      PARTIALLY_USED: 'warning'
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Client Inventory
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Client</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Tissue ID</TableCell>
              <TableCell>Batch ID</TableCell>
              <TableCell>Order #</TableCell>
              <TableCell>Expiration</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inventory.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No inventory found for your clients
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              inventory.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    {item.user?.profile?.clinicName || 
                     `${item.user?.firstName || ''} ${item.user?.lastName || ''}`.trim() || 
                     item.user?.email || 
                     'N/A'}
                  </TableCell>
                  <TableCell>
                    {item.inventorySku?.product?.name || 
                     item.inventorySku?.inventory?.product?.name || 
                     'N/A'}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{item.tissueId || 'N/A'}</TableCell>
                  <TableCell>
                    {item.inventorySku?.inventory?.batchId || 'N/A'}
                  </TableCell>
                  <TableCell>{item.order?.orderNumber || 'N/A'}</TableCell>
                  <TableCell>
                    {item.inventorySku?.inventory?.expirationDate 
                      ? new Date(item.inventorySku.inventory.expirationDate).toLocaleDateString()
                      : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={item.status || 'UNUSED'} 
                      color={getStatusColor(item.status)} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Tooltip title="Mark as Used">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => openUpdateDialog(item, 'USED')}
                        >
                          <UsedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Mark as Discarded">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => openUpdateDialog(item, 'DISCARDED')}
                        >
                          <DiscardedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Status">
                        <IconButton
                          size="small"
                          onClick={() => openUpdateDialog(item)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Update Status Dialog */}
      <Dialog open={updateDialogOpen} onClose={() => setUpdateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#039582', color: 'white' }}>
          Update Inventory Status
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {updatingItem && (
            <Stack spacing={3}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Inventory Item
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {updatingItem.inventorySku?.product?.name || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                  Tissue ID: {updatingItem.tissueId || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Client: {updatingItem.user?.profile?.clinicName || updatingItem.user?.email || 'N/A'}
                </Typography>
              </Box>

              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={updateData.status}
                  onChange={(e) => setUpdateData({ ...updateData, status: e.target.value })}
                  label="Status"
                >
                  <MenuItem value="UNUSED">Unused</MenuItem>
                  <MenuItem value="USED">Used</MenuItem>
                  <MenuItem value="DISCARDED">Discarded</MenuItem>
                  <MenuItem value="PARTIALLY_USED">Partially Used</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                type="number"
                label="Quantity Used"
                value={updateData.quantityUsed}
                onChange={(e) => setUpdateData({ ...updateData, quantityUsed: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 0 }}
                helperText="Enter quantity that has been used"
              />

              <TextField
                fullWidth
                type="number"
                label="Quantity Discarded"
                value={updateData.quantityDiscarded}
                onChange={(e) => setUpdateData({ ...updateData, quantityDiscarded: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 0 }}
                helperText="Enter quantity that has been discarded"
              />

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={updateData.notes}
                onChange={(e) => setUpdateData({ ...updateData, notes: e.target.value })}
                placeholder="Add any notes about this inventory update..."
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setUpdateDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateStatus}
            variant="contained"
            sx={{
              bgcolor: '#039582',
              '&:hover': { bgcolor: '#027a6b' },
              textTransform: 'none'
            }}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RepInventory;
